import { FormattedMessage } from "react-intl";
import { NavDropdown, MenuItem, Tooltip } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import { displayUserName, isInternalUser, url } from "../../utils";
import CONFIGURATION from "../../configuration";
import NavigationDemoData from "./NavigationDemoData";
import CustomOverlayTrigger from "../CustomOverlayTrigger";
import { useFlipperQuery } from "../../hooks/useFlipperQuery";
import { useUser } from "../../hooks/useUserQuery";
import { useSession, useTracking, useConfiguration } from "../../context";

type Props = {
  showDemoData?: boolean;
  showSupportItem?: boolean;
  children?: React.ReactNode;
};

export function NavigationUserMenu({
  showDemoData,
  showSupportItem,
  children,
}: Props) {
  const navigate = useNavigate();
  const configuration = useConfiguration();
  const { pathname } = useLocation();
  const session = useSession();
  const tracking = useTracking();
  const user = useUser();
  const { data: flipper } = useFlipperQuery();

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    tracking.reset();
    session?.deleteSession?.();
    if (
      user?.sso_provider &&
      user?.otp_module === "enabled" &&
      !isInternalUser(user?.email)
    ) {
      const myEndressLogoutUrl = session?.myEndressSsoLogoutUrl;
      const netilionLogoutUrl = `${globalThis.location.origin}${CONFIGURATION.BASE_URL}/logout`;
      globalThis.location.href = `${myEndressLogoutUrl}?post_logout_redirect_uri=${netilionLogoutUrl}`;
    }
  };

  if (!user || !flipper)
    return (
      <Skeleton
        containerTestId="user-navigation-skeleton"
        containerClassName="user-navigation-skeleton"
        height={22}
        width={140}
      />
    );

  const tooltip = (
    <Tooltip id="tooltip">
      <FormattedMessage id="navigation.verified" />
    </Tooltip>
  );

  const title = (
    <div className="username">
      <span>{displayUserName(user, true)}</span>
      {flipper.isVerifyActive && !!user.customers?.length && (
        <CustomOverlayTrigger placement="bottom" overlay={tooltip}>
          <span
            data-testid="user-verified"
            className="icon icon3-eh-radio-button-checked text-info"
          />
        </CustomOverlayTrigger>
      )}
    </div>
  );

  return (
    <NavDropdown
      className="user-navigation-dropdown"
      id="user-navigation-dropdown"
      title={title}
      pullRight
    >
      {children}
      <MenuItem href="/app/id/profile">
        <FormattedMessage id="navigation.user_profile" />
      </MenuItem>
      {user && showDemoData && <NavigationDemoData />}
      <MenuItem href="/app/id/administration">
        <FormattedMessage id="navigation.administration" />
      </MenuItem>
      {showSupportItem && (
        <MenuItem
          id="support-menu-item"
          active={pathname.includes("/support/tickets")}
          onClick={() => navigate(url("/support/tickets"))}
        >
          <FormattedMessage id="navigation.support" />
        </MenuItem>
      )}
      {!!configuration.appCannyBoardToken && (
        <MenuItem
          id="feedback-menu-item"
          active={pathname.includes("/feedback")}
          onClick={() => navigate(url("/feedback"))}
        >
          <FormattedMessage id="navigation.feedback" />
        </MenuItem>
      )}
      <MenuItem href={CONFIGURATION.HELP_URL} target="_blank">
        <FormattedMessage id="navigation.help" />
      </MenuItem>
      <MenuItem href={CONFIGURATION.LEGAL_URL} target="_blank">
        <FormattedMessage id="navigation.legal" />
      </MenuItem>
      <MenuItem onClick={handleLogout} id="user-navigation-dropdown-logout">
        <FormattedMessage id="navigation.logout" />
      </MenuItem>
    </NavDropdown>
  );
}

export default NavigationUserMenu;
